<template>
    <div
        class="
            product-main-image
            fill-width
            fill-height
            no-user-select
        "
    >
        <div
            class="product-main-image-inner"
            :class="{ 'no-thumbs': isMobile }"
        >
            <div
                v-if="!isMobile"
                class="product-main-image-col-1 carousel-v-scroll-parent"
            >
                <ProductMainImageThumbs
                    :key="`prod-image-thumbs-${currentSkuUid}`"
                    :product-images="productImages"
                    :selected-image-uid="selectedImageUid"
                    :youtube-id="product.youtubeId"
                    :image-stack="imageStack"
                    item-class="carousel-v-scroll-item"
                    type="thumbs"
                    orientation="vertical"
                    @on-change="onImgChange"
                ></ProductMainImageThumbs>
            </div>
            <div
                class="product-main-image-col-2"
            >
                <div
                    ref="elProductMainImageContainer"
                    class="product-main-image-container carousel-h-scroll-parent aspect-ratio-4-5"
                >
                    <div
                        ref="elProductImageSlider"
                        class="
                            main-image-slider
                        "
                    >
                        <div
                            v-for="(img, index) in productImages"
                            :key="`main-image-${img.uid || 'default'}`"
                            class="product-image-container carousel-h-scroll-item"
                            :id="`product-main-image-${img.uid}`"
                            :data-uid="img.uid"
                            v-intersection-observer="[
                                onIntersect,
                                {
                                    root: elProductMainImageContainer,
                                    threshold: 0.1
                                }
                            ]"
                        >
                            <div
                                class="
                                    object-fit-contain
                                    aspect-ratio-4-5
                                    position-relative
                                    w-100
                                "
                            >
                                <ProductsLayeredImage
                                    :index="index"
                                    :image-object="img"
                                    :image-stack="imageStack"
                                    :disable-loading-indicator="index > 0"
                                    :top-image-props="{
                                        class: 'object-fit-contain aspect-ratio-4-5 w-100',
                                        disableDropShadow: !img.showDropShadow
                                    }"
                                    :low-images-props="{
                                        class: 'object-fit-contain aspect-ratio-4-5 w-100',
                                        disableDropShadow: true
                                    }"
                                ></ProductsLayeredImage>
                            </div>
                            <v-btn
                                color="secondary"
                                variant="outlined"
                                size="small"
                                aria-label="Zoom Image"
                                @click="zoomImg(img)"
                                icon
                            >
                                <v-icon
                                    :icon="mdiLoupe"
                                    size="24px"
                                ></v-icon>
                            </v-btn>
                        </div>
                        <div
                            v-if="product.youtubeId"
                            class="product-image-container carousel-h-scroll-item d-inline-flex flex-column align-center justify-center"
                            id="product-main-image-0"
                            data-uid="0"
                            v-intersection-observer="[
                                onIntersect,
                                {
                                    root: elProductMainImageContainer,
                                    threshold: 0.1
                                }
                            ]"
                        >
                            <VideoDialog
                                :video-id="product.youtubeId"
                            ></VideoDialog>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="isMobile"
            class="product-main-image-dots"
        >
            <ProductMainImageThumbs
                :key="`prod-image-thumbs-dots-${currentSkuUid}`"
                :product-images="productImages"
                :selected-image-uid="selectedImageUid"
                :youtube-id="product.youtubeId"
                :image-stack="imageStack"
                orientation="horizontal"
                type="dots"
                @on-change="onImgChange"
            ></ProductMainImageThumbs>
        </div>

        <!-- Image Expand Modal/Dialog -->
        <ProductsImageModal
            v-if="dialogShowMainImage"
            v-model="dialogShowMainImage"
            :key="`modal-main-image-${selectedImageUid || 'default'}`"
            :selected-image-uid="selectedImageUid"
            :product-images="productImages"
            :image-object="selectedImage"
            :image-stack="imageStack"
            :type="isMobile ? 'dots' : 'thumbs'"
            @on-exit="onImgModalClose"
        ></ProductsImageModal>
    </div>
</template>

<script lang="ts" setup>
    import type {
        Product,
        Sku,
        FullImage,
        FullImageStack
    } from '~/types';
    import { useFullImage } from '~/composables';
    import { vIntersectionObserver } from '@vueuse/components';
    import ProductsLayeredImage from '~/components/products/ProductsLayeredImage/ProductsLayeredImage.vue';
    import ProductMainImageThumbs from '~/components/products/ProductMainImageThumbs/ProductMainImageThumbs.vue';
    import ProductsImageModal from '~/components/products/ProductsImageModal/ProductsImageModal.vue';
    import VideoDialog from '~/components/video/VideoDialog/VideoDialog.vue';
    import { useIsMobile } from '~/composables';
    import { mdiLoupe } from '@mdi/js';

    const props = withDefaults(
        defineProps<{
            product: Product;
            currentSkuUid?: Nilish<string>;
            imageStack?: Nilish<FullImageStack>;
        }>(),
        {
            currentSkuUid: null
        }
    );

    const {
        isMobile
    } = useIsMobile();
    const {
        getFallbackImage
    } = useFullImage();

    const elProductMainImageContainer = shallowRef<HTMLElement>();
    const elProductImageSlider = shallowRef<HTMLElement>();

    const dialogShowMainImage = ref(false);
    const fallbackImageObj = getFallbackImage();
    const skuNotAvailable = ref(false);
    const productImages = ref<Nullable<FullImage[]>>(null);
    const selectedImageUid = ref<Nullable<string>>(null);
    const selectedImage = ref<Nullable<FullImage>>(null);

    const unWatchCurrentSku = watch(
        () => props.currentSkuUid,
        (uid: Nilish<string>, oldUid: Nilish<string>) => {
            if (uid === null) {
                productImages.value = [fallbackImageObj];
                skuNotAvailable.value = true;
                return;
            }

            skuNotAvailable.value = false;

            if (uid === oldUid) {
                return;
            }

            const currentSkuObj: Nullable<Sku> = props.product.skus.find((o) => o.uid === uid) ?? null;
            const images = productImages.value = (currentSkuObj && currentSkuObj.images?.length)
                ? currentSkuObj.images
                : [fallbackImageObj];

            selectedImageUid.value = images[0]?.uid ?? null;
        },
        { immediate: true }
    );

    function onImgChange(imgIdSel: string): void {
        const target = elProductMainImageContainer.value?.querySelector(imgIdSel);

        if (!target) {
            return;
        }

        target.scrollIntoView({
            behavior: 'instant',
            block: 'nearest',
            inline: 'nearest'
        });
    }

    function onIntersect(entries: IntersectionObserverEntry[]): void {
        const entry = entries[0];
        if (entry && entry.target instanceof HTMLElement && entry.isIntersecting) {
            selectedImageUid.value = entry.target.dataset['uid']!;
        }
    }

    function zoomImg(img: FullImage): void {
        selectedImage.value = img;
        dialogShowMainImage.value = true;
    }

    function onImgModalClose(): void {
        selectedImage.value = null;
    }

    onBeforeUnmount(() => {
        unWatchCurrentSku?.();
    });
</script>

<style lang="scss"></style>
